import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Calculator, CheckCheck, FileText, Landmark, Lock, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Indicador, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { CampoData, EntradaSensivel, Rotulo, Selecao, type DefCampo } from '../../componentes/formulario';
import { Chatter } from '../../componentes/chatter';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { api, cabecalhoEmpresa, del, get, getTodos, mensagemDeErro, patch, post, textoDeErroGravado } from '../../nucleo/api';
import { precisaEscolherEmpresa } from '../../nucleo/empresa/empresa-do-registro';
import { data as formatarData, dinheiro, hojeIso, numero, nomePessoa, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { CamposLocais, ErroGeral, useEnvio } from './formulario-local';
import { COR_ESTADO, invalidarFolha, limpar, rotuloCompetencia, type Competencia, type CompetenciaDetalhe, type Variavel } from './tipos';
import { anunciarOcultos } from '../../componentes/sensivel';

/** M8.3: barra de progresso do job de fechamento. */
/**
 * Nome do centro no rateio da competência (review r14 segurança B3): sem `centro_custo_id`, "Sem centro de custo" no idioma;
 * com o centro e o nome nulo (quem lê não tem acesso ao Financeiro), "Centro oculto (sem acesso ao Financeiro)"; senão, o nome.
 */
function NomeCentroRateio({ centro }: { centro: CompetenciaDetalhe['centros'][number] }) {
  const { t } = useTranslation('folha');
  const oculto = centro.centro_custo_id !== null && !centro.centro_nome;
  const nome = centro.centro_custo_id === null ? t('centros.semCentro') : centro.centro_nome || t('centros.centroOculto');
  return (
    <span className={`truncate${oculto ? ' text-tinta-2' : ''}`} title={nome} data-centro-oculto={oculto || undefined}>
      {nome}
    </span>
  );
}

function ProgressoFechamento({ detalhe, podeFechar, aoTentar, tentando }: { detalhe: CompetenciaDetalhe; podeFechar: boolean; aoTentar: () => void; tentando: boolean }) {
  const { t } = useTranslation('folha');
  const job = detalhe.job;
  const pct = job?.estado === 'concluido' ? 100 : (job?.progresso ?? 0);
  const erro = job?.estado === 'erro';
  return (
    <section className="rounded-xl border border-borda bg-sup-2 p-4" aria-label={t('fechamento.titulo')}>
      <div className="mb-2 flex items-center justify-between gap-3 text-sm">
        <span className="font-medium">{erro ? t('fechamento.erro') : t('fechamento.andamento')}</span>
        <span className="numero text-tinta-3">{pct}%</span>
      </div>
      <div role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={t('fechamento.titulo')} className="h-2 w-full overflow-hidden rounded-full bg-sup-3">
        <div className={`h-2 rounded-full transition-all ${erro ? 'bg-perigo' : 'bg-acento-forte'}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-2 text-xs text-tinta-3">{erro ? (textoDeErroGravado(job?.erro) || t('fechamento.erroTexto')) : t('fechamento.passos')}</p>
      {erro && podeFechar && (
        <Botao className="mt-3" variante="primario" carregando={tentando} onClick={aoTentar}>
          {t('acoes.tentarFecharDeNovo')}
        </Botao>
      )}
    </section>
  );
}

/** Lançamentos variáveis da competência: lista e formulário de novo lançamento. */
function Variaveis({ competencia, editavel }: { competencia: CompetenciaDetalhe; editavel: boolean }) {
  const { t } = useTranslation('folha');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [valores, setValores] = useState<Record<string, unknown>>({ quantidade: 1 });
  const [rodada, setRodada] = useState(0);
  const envio = useEnvio();
  const chave = ['folha', 'variaveis', competencia.id];
  const q = useQuery({ queryKey: chave, queryFn: () => getTodos<Variavel>('/folha/variaveis', { competencia_id: competencia.id }) });
  const remover = useMutation({
    mutationFn: (id: string) => del(`/folha/variaveis/${id}`),
    onSuccess: () => invalidarFolha(qc),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const campos: DefCampo[] = [
    { nome: 'funcionario_id', rotulo: t('campos.funcionario'), tipo: 'busca', obrigatorio: true, largura: 'meia', busca: { caminho: '/pessoas/funcionarios', params: { estado: 'ativo' }, rotulo: (i) => String(i.nome) } },
    { nome: 'evento_id', rotulo: t('campos.evento'), tipo: 'busca', obrigatorio: true, largura: 'meia', busca: { caminho: '/folha/eventos', params: { tipo: 'variavel', ativo: 'sim', lancavel: 'sim' }, rotulo: (i) => `${i.codigo} ${i.nome}` } },
    { nome: 'quantidade', rotulo: t('campos.quantidade'), tipo: 'numero', largura: 'terco', min: 0.01, passo: 0.01, ajuda: t('campos.quantidadeAjuda') },
    { nome: 'valor_centavos', rotulo: t('campos.valorFinal'), tipo: 'dinheiro', largura: 'terco', ajuda: t('campos.valorFinalAjuda') },
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'texto', largura: 'terco' },
  ];

  async function lancar() {
    if (!envio.validar(campos, valores)) return;
    const ok = await envio.enviar(() => post('/folha/variaveis', { competencia_id: competencia.id, ...limpar({ ...valores, quantidade: valores.quantidade || undefined }) }));
    if (ok) {
      setValores({ quantidade: 1 });
      setRodada((r) => r + 1);
      await invalidarFolha(qc);
      toast.sucesso(t('variaveis.lancado'));
    }
  }

  return (
    <Cartao titulo={t('variaveis.titulo')}>
      {editavel && s.pode('folha.editar') && (
        <form
          noValidate
          className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-6"
          onSubmit={(e) => {
            e.preventDefault();
            void lancar();
          }}
        >
          <ErroGeral texto={envio.geral} />
          <CamposLocais key={rodada} campos={campos} valores={valores} erros={envio.erros} aoMudar={(n, x) => setValores((a) => ({ ...a, [n]: x }))} />
          <div className="sm:col-span-6">
            <Botao type="submit" icone={<Plus className="size-4" />} carregando={envio.enviando}>
              {t('acoes.lancarVariavel')}
            </Botao>
          </div>
        </form>
      )}
      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      ) : q.data.itens.length === 0 ? (
        <p className="text-sm text-tinta-3">{t('variaveis.vazio')}</p>
      ) : (
        <ul className="divide-y divide-borda">
          {q.data.itens.map((v) => (
            <li key={v.id} className="flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2 text-sm">
              <span className="min-w-0">
                <span className="font-medium">{nomePessoa(v.funcionario_nome)}</span>
                <span className="text-tinta-3">
                  {' '}
                  {v.evento_codigo} {v.evento_nome}
                </span>
                {v.descricao && <span className="block truncate text-xs text-tinta-3" title={textoLivre(v.descricao)}>{textoLivre(v.descricao)}</span>}
              </span>
              <span className="flex items-center gap-3">
                <span className="numero text-tinta-2">{v.valor_centavos !== null ? dinheiro(v.valor_centavos, competencia.moeda) : numero(v.quantidade, 2)}</span>
                {v.origem_modulo ? (
                  <span className="text-xs text-tinta-3">{t('variaveis.doDespesas')}</span>
                ) : editavel && s.pode('folha.editar') && (
                  <button type="button" aria-label={tn('comum.excluir')} onClick={() => remover.mutate(v.id)} className="rounded p-1 text-tinta-3 hover:text-perigo">
                    <Trash2 className="size-4" />
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Cartao>
  );
}

/** Edição de data de pagamento e observações enquanto a competência não fechou. */
function DadosCompetencia({ competencia }: { competencia: CompetenciaDetalhe }) {
  const { t } = useTranslation('folha');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [pagamento, setPagamento] = useState(competencia.data_pagamento ?? '');
  const [observacoes, setObservacoes] = useState(competencia.observacoes ?? '');
  const envio = useEnvio();
  async function salvar() {
    const ok = await envio.enviar(() => patch(`/folha/competencias/${competencia.id}`, { data_pagamento: pagamento || null, observacoes: observacoes || null }));
    if (ok) {
      await invalidarFolha(qc);
      toast.sucesso(tn('comum.salvo'));
    }
  }
  return (
    <Cartao titulo={t('competencias.dados')}>
      <form
        noValidate
        className="grid grid-cols-1 gap-3 sm:grid-cols-6"
        onSubmit={(e) => {
          e.preventDefault();
          void salvar();
        }}
      >
        <ErroGeral texto={envio.geral} />
        <div className="sm:col-span-2">
          <Rotulo id="comp-pagamento" rotulo={t('campos.dataPagamento')} erro={envio.erros.data_pagamento} ajuda={t('campos.dataPagamentoAjuda')}>
            <CampoData id="comp-pagamento" type="date" value={pagamento} onChange={(e) => setPagamento(e.target.value)} />
          </Rotulo>
        </div>
        <div className="sm:col-span-4">
          <Rotulo id="comp-obs" rotulo={t('campos.observacoes')} erro={envio.erros.observacoes}>
            <EntradaSensivel mascara={null} livre id="comp-obs" value={observacoes} maxLength={2000} onChange={(e) => setObservacoes(e.target.value)} />
          </Rotulo>
        </div>
        <div className="sm:col-span-6">
          <Botao type="submit" carregando={envio.enviando}>
            {tn('comum.salvar')}
          </Botao>
        </div>
      </form>
    </Cartao>
  );
}

/** Detalhe da competência: totais, ações do ciclo (calcular, conferir, fechar, reabrir), progresso do fechamento, rateio e variáveis. */
function DetalheCompetencia({ id }: { id: string }) {
  const { t } = useTranslation('folha');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [confirmar, setConfirmar] = useState<'fechar' | 'reabrir' | null>(null);
  const q = useQuery({
    queryKey: ['folha', 'competencia', id],
    queryFn: () => get<CompetenciaDetalhe>(`/folha/competencias/${id}`),
    // Enquanto o job roda, o progresso é lido a cada segundo.
    refetchInterval: (c) => (c.state.data?.estado === 'fechando' ? 1000 : false),
  });
  const aoTerminar = async () => {
    await invalidarFolha(qc);
  };
  const useAcao = (caminho: string, sucesso: string) =>
    useMutation({
      mutationFn: () => post<{ pulados?: unknown[] } | undefined>(`/folha/competencias/${id}/${caminho}`),
      onSuccess: async (r) => {
        await aoTerminar();
        toast.sucesso(sucesso);
        // Lote de 19/10: quem está sem CPF ou sem admissão ficou fora do cálculo (o motivo também vai ao histórico).
        const pulados = r?.pulados?.length ?? 0;
        if (pulados) toast.info(t('competencias.pulados', { count: pulados }));
      },
      onError: (e) => toast.erro(mensagemDeErro(e)),
    });
  const calcular = useAcao('calcular', t('competencias.calculada'));
  const conferir = useAcao('conferir', t('competencias.conferida'));
  const fechar = useAcao('fechar', t('competencias.fechamentoIniciado'));
  const reabrir = useAcao('reabrir', t('competencias.reaberta'));

  if (q.isPending) return <Carregando />;
  if (q.isError) return <Erro erro={q.error} aoTentar={() => q.refetch()} />;
  const c = q.data;
  const editavel = c.estado === 'aberta' || c.estado === 'calculada' || c.estado === 'conferida';
  const podeEditar = s.pode('folha.editar');
  const podeFechar = s.pode('folha.fechar');
  const moeda = c.moeda;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Selo cor={COR_ESTADO[c.estado]}>{t(`estado.${c.estado}`)}</Selo>
        <span className="text-sm text-tinta-3">{c.data_pagamento ? t('competencias.pagamentoEm', { data: formatarData(c.data_pagamento) }) : t('competencias.pagamentoPadrao')}</span>
        {c.fechada_em && <span className="text-sm text-tinta-3">{t('competencias.fechadaEm', { data: formatarData(c.fechada_em), nome: c.fechada_por_nome ?? '-' })}</span>}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Indicador rotulo={t('totais.funcionarios')} valor={numero(c.funcionarios)} />
        <Indicador rotulo={t('totais.proventos')} valor={dinheiro(c.proventos_centavos, moeda)} cor="verde" />
        <Indicador rotulo={t('totais.descontos')} valor={dinheiro(c.descontos_centavos, moeda)} cor="laranja" />
        <Indicador rotulo={t('totais.liquido')} valor={dinheiro(c.liquido_centavos, moeda)} />
        <Indicador rotulo={t('totais.encargos')} valor={dinheiro(c.encargos_centavos, moeda)} cor="ciano" />
        <Indicador rotulo={t('totais.custo')} valor={dinheiro(c.custo_centavos, moeda)} detalhe={t('totais.custoAjuda')} />
      </div>

      <div className="flex flex-wrap gap-2">
        {editavel && podeFechar && (
          <Botao icone={<Calculator className="size-4" />} carregando={calcular.isPending} onClick={() => calcular.mutate()}>
            {c.estado === 'aberta' ? t('acoes.calcular') : t('acoes.recalcular')}
          </Botao>
        )}
        {c.estado === 'calculada' && podeFechar && (
          <Botao variante="primario" icone={<CheckCheck className="size-4" />} carregando={conferir.isPending} onClick={() => conferir.mutate()}>
            {t('acoes.conferir')}
          </Botao>
        )}
        {c.estado === 'conferida' && podeFechar && (
          <Botao variante="primario" icone={<Lock className="size-4" />} onClick={() => setConfirmar('fechar')}>
            {t('acoes.fechar')}
          </Botao>
        )}
        {(c.estado === 'calculada' || c.estado === 'conferida' || (c.estado === 'fechando' && c.job?.estado === 'erro')) && podeFechar && (
          <Botao variante="fantasma" icone={<RotateCcw className="size-4" />} onClick={() => setConfirmar('reabrir')}>
            {t('acoes.reabrir')}
          </Botao>
        )}
        {(c.estado === 'fechada' || c.funcionarios > 0) && (
          <Link
            to={`/pessoas/folha/holerites?competencia=${c.id}`}
            className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg border border-borda bg-sup-2 px-4 text-sm font-medium text-tinta-1 hover:bg-sup-3"
          >
            <FileText className="size-4" aria-hidden />
            {t('acoes.verHolerites')}
          </Link>
        )}
      </div>
      {c.estado === 'aberta' && <p className="text-xs text-tinta-3">{t('competencias.dicaAberta')}</p>}
      {c.estado === 'calculada' && <p className="text-xs text-tinta-3">{t('competencias.dicaCalculada')}</p>}
      {c.estado === 'conferida' && <p className="text-xs text-tinta-3">{t('competencias.dicaConferida')}</p>}

      {/* Review r5: fechamento recusado por regra volta para conferida com o erro; o botão Fechar acima já tenta de novo. */}
      {(c.estado === 'fechando' || c.job?.estado === 'erro') && (
        <ProgressoFechamento detalhe={c} podeFechar={podeFechar && c.estado === 'fechando'} aoTentar={() => fechar.mutate()} tentando={fechar.isPending} />
      )}
      {c.estado === 'fechada' && c.job?.resultado && (
        <p className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2">
          {t('fechamento.resumo', { pdfs: Number(c.job.resultado.pdfs ?? 0), emails: Number(c.job.resultado.emails ?? 0), lancamentos: Number(c.job.resultado.lancamentos ?? 0) })}
        </p>
      )}

      {c.centros.length > 0 && (
        <Cartao titulo={t('competencias.rateio')}>
          <ul className="divide-y divide-borda">
            {c.centros.map((x) => (
              <li key={x.id} className="flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2 text-sm">
                <span className="flex min-w-0 items-center gap-2">
                  <Landmark className="size-4 shrink-0 text-tinta-3" aria-hidden />
                  <NomeCentroRateio centro={x} />
                  <span className="text-xs text-tinta-3">{t('competencias.pessoas', { count: x.funcionarios })}</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="numero">{anunciarOcultos(dinheiro(x.custo_centavos, moeda))}</span>
                  {x.lancamento_id && <Selo cor="verde">{t('competencias.lancado')}</Selo>}
                </span>
              </li>
            ))}
          </ul>
        </Cartao>
      )}

      {editavel && podeEditar && <DadosCompetencia key={`${c.id}-${c.data_pagamento}-${c.observacoes}`} competencia={c} />}
      <Variaveis competencia={c} editavel={editavel} />
      <Chatter
        entidade="folha.competencia"
        registroId={c.id}
        rotulosCampos={{ estado: t('campos.situacao'), funcionarios: t('totais.funcionarios'), liquido_centavos: t('totais.liquido'), data_pagamento: t('campos.dataPagamento'), observacoes: t('campos.observacoes') }}
      />

      <Confirmar
        aberto={confirmar === 'fechar'}
        aoFechar={() => setConfirmar(null)}
        aoConfirmar={() => {
          setConfirmar(null);
          fechar.mutate();
        }}
        titulo={t('confirmar.fechar', { competencia: rotuloCompetencia(c.ano_mes) })}
        texto={t('confirmar.fecharTexto')}
        carregando={fechar.isPending}
      />
      <Confirmar
        aberto={confirmar === 'reabrir'}
        aoFechar={() => setConfirmar(null)}
        aoConfirmar={() => {
          setConfirmar(null);
          reabrir.mutate();
        }}
        titulo={t('confirmar.reabrir')}
        texto={t('confirmar.reabrirTexto')}
        perigoso
        carregando={reabrir.isPending}
      />
    </div>
  );
}

/** M8.5: centro de custo que recebe o custo da folha de cada departamento. */
function CentrosDepartamento({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation('folha');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [escolhas, setEscolhas] = useState<Record<string, string>>({});
  const envio = useEnvio();
  const q = useQuery({
    queryKey: ['folha', 'centros-departamento'],
    queryFn: () =>
      getTodos<
        { departamento_id: string; departamento_nome: string; centro_custo_id: string | null },
        {
          itens: { departamento_id: string; departamento_nome: string; centro_custo_id: string | null }[];
          proximo_cursor?: string | null;
          centros: { id: string; nome: string }[];
          /** Review r13 segurança (B2): quem não lê o Financeiro recebe a lista vazia e o nome nulo; o id do vínculo fica. */
          centros_ocultos?: boolean;
        }
      >('/folha/centros-departamento'),
    enabled: aberto,
  });
  const valorDe = (dep: string, atual: string | null) => escolhas[dep] ?? atual ?? '';
  const ocultos = !!q.data?.centros_ocultos;
  // Sem acesso ao Financeiro (review r13): o vínculo gravado aparece como "Configurado (sem acesso ao Financeiro)", sem o nome, e nada é editável.
  const opcoesDe = (atual: string | null) => {
    const opcoes = (q.data?.centros ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }));
    if (ocultos && atual && !opcoes.some((o) => o.valor === atual)) opcoes.unshift({ valor: atual, rotulo: t('centros.configuradoOculto') });
    return opcoes;
  };
  async function salvar() {
    if (!q.data) return;
    const centros = q.data.itens.map((d) => ({ departamento_id: d.departamento_id, centro_custo_id: valorDe(d.departamento_id, d.centro_custo_id) || null }));
    const ok = await envio.enviar(() => api('PUT', '/folha/centros-departamento', { centros }));
    if (ok) {
      setEscolhas({});
      await qc.invalidateQueries({ queryKey: ['folha'] });
      toast.sucesso(tn('comum.salvo'));
      aoFechar();
    }
  }
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('centros.titulo')}
      largura="lg"
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={envio.enviando} disabled={!q.data?.itens.length || ocultos} onClick={() => void salvar()}>
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <p className="mb-3 text-sm text-tinta-2">{t('centros.texto')}</p>
      <div className="flex flex-col gap-3">
        <ErroGeral texto={envio.geral} />
        {ocultos && (
          <p role="note" data-folha-centros-sem-acesso className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2">
            {t('centros.semAcesso')}
          </p>
        )}
        {q.isPending ? (
          <Carregando />
        ) : q.isError ? (
          <Erro erro={q.error} aoTentar={() => q.refetch()} />
        ) : q.data.itens.length === 0 ? (
          <p className="text-sm text-tinta-3">{t('centros.vazio')}</p>
        ) : (
          q.data.itens.map((d) => (
            <Rotulo key={d.departamento_id} id={`centro-${d.departamento_id}`} rotulo={d.departamento_nome}>
              <Selecao
                id={`centro-${d.departamento_id}`}
                vazio={t('centros.semCentro')}
                disabled={ocultos}
                opcoes={opcoesDe(d.centro_custo_id)}
                value={valorDe(d.departamento_id, d.centro_custo_id)}
                onChange={(e) => setEscolhas((a) => ({ ...a, [d.departamento_id]: e.target.value }))}
              />
            </Rotulo>
          ))
        )}
      </div>
    </Modal>
  );
}

function AbrirCompetencia({ aberto, aoFechar, aoAbrir }: { aberto: boolean; aoFechar: () => void; aoAbrir: (id: string, anoMes: string) => void }) {
  const { t } = useTranslation('folha');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [anoMes, setAnoMes] = useState(hojeIso().slice(0, 7));
  const [pagamento, setPagamento] = useState('');
  const [observacoes, setObservacoes] = useState('');
  // V2 verticais: a competência é de uma empresa; sem empresa na aba (sem filtro, várias permitidas), ela é escolhida aqui.
  const s = useSessao();
  const escolherEmpresa = precisaEscolherEmpresa(s.empresas);
  const [empresa, setEmpresa] = useState('');
  const envio = useEnvio();
  async function abrir() {
    const exigidos: DefCampo[] = [{ nome: 'ano_mes', rotulo: t('campos.competencia'), tipo: 'texto', obrigatorio: true }];
    if (escolherEmpresa) exigidos.push({ nome: 'empresa', rotulo: tn('empresa.rotulo'), tipo: 'selecao', obrigatorio: true });
    if (!envio.validar(exigidos, { ano_mes: anoMes, empresa })) return;
    let id = '';
    const ok = await envio.enviar(async () => {
      const r = await post<{ id: string }>('/folha/competencias', { ano_mes: anoMes, data_pagamento: pagamento || null, observacoes: observacoes || null }, cabecalhoEmpresa(escolherEmpresa ? empresa : null));
      id = r.id;
    });
    if (ok) {
      await invalidarFolha(qc);
      toast.sucesso(t('competencias.aberta'));
      setPagamento('');
      setObservacoes('');
      aoFechar();
      aoAbrir(id, anoMes);
    }
  }
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('acoes.abrirCompetencia')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-abrir-competencia" carregando={envio.enviando}>
            {t('acoes.abrir')}
          </Botao>
        </>
      }
    >
      <form
        id="form-abrir-competencia"
        noValidate
        className="grid grid-cols-1 gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void abrir();
        }}
      >
        <ErroGeral texto={envio.geral} />
        {escolherEmpresa && (
          <Rotulo id="nova-empresa" rotulo={tn('empresa.rotulo')} obrigatorio erro={envio.erros.empresa} ajuda={tn('empresa.ajudaRegistro')}>
            <Selecao id="nova-empresa" vazio="" value={empresa} opcoes={(s.empresas?.opcoes ?? []).map((e) => ({ valor: e.id, rotulo: e.nome }))} onChange={(e) => setEmpresa(e.target.value)} />
          </Rotulo>
        )}
        <Rotulo id="nova-ano-mes" rotulo={t('campos.competencia')} obrigatorio erro={envio.erros.ano_mes}>
          <CampoData id="nova-ano-mes" type="month" value={anoMes} onChange={(e) => setAnoMes(e.target.value)} />
        </Rotulo>
        <Rotulo id="nova-pagamento" rotulo={t('campos.dataPagamento')} erro={envio.erros.data_pagamento} ajuda={t('campos.dataPagamentoAjuda')}>
          <CampoData id="nova-pagamento" type="date" value={pagamento} onChange={(e) => setPagamento(e.target.value)} />
        </Rotulo>
        <Rotulo id="nova-obs" rotulo={t('campos.observacoes')} erro={envio.erros.observacoes}>
          <EntradaSensivel mascara={null} livre id="nova-obs" value={observacoes} maxLength={2000} onChange={(e) => setObservacoes(e.target.value)} />
        </Rotulo>
      </form>
    </Modal>
  );
}

/** M8.3: competências mensais da folha (lista, abrir, lançar variáveis, calcular, conferir, fechar). */
export function Competencias() {
  const { t } = useTranslation('folha');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const [novo, setNovo] = useState(false);
  const [centros, setCentros] = useState(false);
  const [detalhe, setDetalhe] = useState<string | null>(null);
  const [titulo, setTitulo] = useState('');

  const colunas: Coluna<Competencia>[] = [
    { chave: 'ano_mes', rotulo: t('campos.competencia'), mono: true, principal: true, render: (c) => <span className="font-medium">{rotuloCompetencia(c.ano_mes)}</span> },
    { chave: 'estado', rotulo: t('campos.situacao'), render: (c) => <Selo cor={COR_ESTADO[c.estado]}>{t(`estado.${c.estado}`)}</Selo> },
    { chave: 'funcionarios', rotulo: t('totais.funcionarios'), alinhar: 'direita', mono: true },
    { chave: 'proventos_centavos', rotulo: t('totais.proventos'), alinhar: 'direita', mono: true, render: (c) => dinheiro(c.proventos_centavos, c.moeda) },
    { chave: 'descontos_centavos', rotulo: t('totais.descontos'), alinhar: 'direita', mono: true, render: (c) => dinheiro(c.descontos_centavos, c.moeda) },
    { chave: 'liquido_centavos', rotulo: t('totais.liquido'), alinhar: 'direita', mono: true, render: (c) => <span className="font-medium">{anunciarOcultos(dinheiro(c.liquido_centavos, c.moeda))}</span> },
    { chave: 'custo_centavos', rotulo: t('totais.custo'), alinhar: 'direita', mono: true, oculta: true, render: (c) => dinheiro(c.custo_centavos, c.moeda) },
    { chave: 'data_pagamento', rotulo: t('campos.dataPagamento'), oculta: true, render: (c) => formatarData(c.data_pagamento) },
  ];

  return (
    <>
      <AcaoPrimaria>
        {/* Val5 (29/10): o centro de custo por departamento é configuração da folha (`folha.configurar`). */}
        {s.pode('folha.configurar') && (
          <Botao icone={<Landmark className="size-4" />} onClick={() => setCentros(true)}>
            {t('acoes.centrosCusto')}
          </Botao>
        )}
        {s.pode('folha.criar') && (
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNovo(true)}>
            {t('acoes.abrirCompetencia')}
          </Botao>
        )}
      </AcaoPrimaria>
      <Tabela<Competencia>
        tela="folha.competencias"
        caminho="/folha/competencias"
        colunas={colunas}
        aoClicar={(c) => {
          setTitulo(rotuloCompetencia(c.ano_mes));
          setDetalhe(c.id);
        }}
        vazio={<Vazio texto={t('vazio.competencias')} />}
      />
      <Drawer aberto={!!detalhe} aoFechar={() => setDetalhe(null)} largura="lg" titulo={t('competencias.titulo', { competencia: titulo })}
        rodape={
          <Botao variante="fantasma" onClick={() => setDetalhe(null)}>
            {tn('comum.fechar')}
          </Botao>
        }
      >
        {detalhe && <DetalheCompetencia key={detalhe} id={detalhe} />}
      </Drawer>
      <AbrirCompetencia
        aberto={novo}
        aoFechar={() => setNovo(false)}
        aoAbrir={(id, anoMes) => {
          setTitulo(rotuloCompetencia(anoMes));
          setDetalhe(id);
        }}
      />
      <CentrosDepartamento aberto={centros} aoFechar={() => setCentros(false)} />
    </>
  );
}
